import type { Storey } from '@shared/models/design/storey.model';
import type { Point2D, Polygon2D } from '@shared/models/geometry.model';
import { getSolidShapes } from '@shared/helpers/design/cutout.helpers';
import {
  isPointInPolygon,
  projectPointOntoSegmentClamped,
} from '@shared/helpers/geometry/geometry.helpers';

export const BUILDING_ROTATION_HANDLE_INNER_PX = 5;
export const BUILDING_ROTATION_HANDLE_OUTER_PX = 28;

/** Cmd on macOS and Ctrl elsewhere temporarily disable building angle snapping. */
export const isBuildingRotationSnapDisabled = (
  event: Pick<MouseEvent, 'metaKey' | 'ctrlKey'>,
  isMac: boolean,
): boolean => (isMac ? event.metaKey : event.ctrlKey);

/** Use the longest exterior wall as the building's stable screen orientation. */
export const getBuildingRotationReferenceEdge = (
  storeys: readonly Storey[],
): [Point2D, Point2D] | undefined => {
  let edge: [Point2D, Point2D] | undefined;
  let longest = 0;
  for (const storey of storeys) {
    for (const shape of getSolidShapes(storey)) {
      shape.points.forEach((start, index) => {
        const end = shape.points[(index + 1) % shape.points.length];
        if (!end) return;
        const length = Math.hypot(end[0] - start[0], end[1] - start[1]);
        if (length <= longest) return;
        longest = length;
        edge = [start, end];
      });
    }
  }
  return edge;
};

/**
 * Round the drag to whole degrees, or align the projected main wall to screen
 * multiples of 5° with Shift. Projection accounts for bearing and perspective.
 */
export const snapBuildingRotationRadians = ({
  radians,
  disabled,
  shiftKey,
  center,
  referenceEdge,
  project,
}: {
  radians: number;
  disabled: boolean;
  shiftKey: boolean;
  center: Point2D;
  referenceEdge: [Point2D, Point2D] | undefined;
  project: (point: Point2D) => Point2D | undefined;
}): number => {
  if (disabled) return radians;
  if (!shiftKey || !referenceEdge) {
    return (Math.round((radians * 180) / Math.PI) * Math.PI) / 180;
  }
  const screenAngle = (rotation: number): number | undefined => {
    const start = project(
      rotatePointAroundCenter(referenceEdge[0], center, rotation),
    );
    const end = project(
      rotatePointAroundCenter(referenceEdge[1], center, rotation),
    );
    if (
      !start ||
      !end ||
      Math.hypot(end[0] - start[0], end[1] - start[1]) < 1e-6
    )
      return undefined;
    return Math.atan2(end[1] - start[1], end[0] - start[0]);
  };
  const angle = screenAngle(radians);
  if (angle === undefined) return radians;
  const step = Math.PI / 36;
  const target = Math.round(angle / step) * step;
  const signedDifference = (left: number, right: number): number =>
    Math.atan2(Math.sin(left - right), Math.cos(left - right));
  let result = radians;
  // Invert the actual projection locally; a tilted camera does not preserve angles.
  for (let iteration = 0; iteration < 8; iteration++) {
    const current = screenAngle(result);
    const next = screenAngle(result + 1e-4);
    if (current === undefined || next === undefined) return radians;
    const error = signedDifference(current, target);
    if (Math.abs(error) < 1e-7) return result;
    const derivative = signedDifference(next, current) / 1e-4;
    if (Math.abs(derivative) < 1e-6) return radians;
    result -= Math.max(-0.2, Math.min(0.2, error / derivative));
  }
  return radians;
};

/** Exterior band around the union of projected roof, floor and wall faces. */
export const isPointerOutsideBuildingContour = ({
  pointer,
  faces,
  innerPx = BUILDING_ROTATION_HANDLE_INNER_PX,
  outerPx = BUILDING_ROTATION_HANDLE_OUTER_PX,
}: {
  pointer: Point2D;
  faces: readonly Point2D[][];
  innerPx?: number;
  outerPx?: number;
}): boolean => {
  if (faces.some((face) => isPointInPolygon(pointer, face))) return false;
  let distance = Infinity;
  for (const face of faces) {
    for (let index = 0; index < face.length; index++) {
      const start = face[index];
      const end = face[(index + 1) % face.length];
      if (!start || !end) continue;
      const closest = projectPointOntoSegmentClamped(pointer, start, end);
      distance = Math.min(
        distance,
        Math.hypot(pointer[0] - closest[0], pointer[1] - closest[1]),
      );
    }
  }
  return distance >= innerPx && distance <= outerPx;
};

export function getBuildingRotationTargets(storeys: readonly Storey[]): {
  storeyId: string;
  storeyLevel: number;
  shapeIds: string[];
}[] {
  return storeys.map((storey, storeyLevel) => ({
    storeyId: storey.id,
    storeyLevel,
    shapeIds: storey.shapes.map((shape) => shape.id),
  }));
}

/** Bounding-box midpoint shared by every polygon in the selected building. */
export function getBuildingRotationCenter(
  storeys: readonly Storey[],
): Point2D | undefined {
  const points = storeys.flatMap((storey) =>
    storey.shapes.flatMap((shape) => shape.points ?? []),
  );
  if (!points.length) return undefined;
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const [x, y] of points) {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }
  return [(minX + maxX) / 2, (minY + maxY) / 2];
}

export function rotatePointAroundCenter(
  point: Point2D,
  center: Point2D,
  radians: number,
): Point2D {
  const dx = point[0] - center[0];
  const dy = point[1] - center[1];
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return [center[0] + dx * cos - dy * sin, center[1] + dx * sin + dy * cos];
}

export function rotatePolygonAroundCenter(
  points: Polygon2D,
  center: Point2D,
  radians: number,
): Polygon2D {
  return points.map((point) =>
    rotatePointAroundCenter(point, center, radians),
  ) as Polygon2D;
}

/** Smallest signed angular change, avoiding a jump at ±π. */
export function getRotationDeltaRadians(
  center: Point2D,
  start: Point2D,
  current: Point2D,
): number {
  const startAngle = Math.atan2(start[1] - center[1], start[0] - center[0]);
  const currentAngle = Math.atan2(
    current[1] - center[1],
    current[0] - center[0],
  );
  return Math.atan2(
    Math.sin(currentAngle - startAngle),
    Math.cos(currentAngle - startAngle),
  );
}
